import {useMutation, useQuery} from '@tanstack/react-query';
import {snackbarAtom} from 'atoms/snackbar';
import {
  EditorDoc,
  EditordocService,
  EditorDocStatus,
  EditorDocStrategyAssociation,
  EditorDocStrategyAssociationCreate,
  EditorDocStrategyAssociationUpdate,
  EditorDocTemplateType,
  EditorDocUpdate,
  EditorWorkstreamAssociation,
  EditorWorkstreamType,
} from 'client';
import {t} from 'i18next';
import {useAtom, useSetAtom} from 'jotai';
import {useCallback} from 'react';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';

import {recentPlanningDocsAtom} from './utils';

const MAX_RECENT_PLANNING_DOCS = 20;

export const usePlanning = (planningId: string) => {
  const {
    data: planning,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['planning', planningId],
    queryFn: () => getService(EditordocService).getApiV1EditordocEncryptedIdGet(planningId),
    enabled: !!planningId,
    placeholderData: (previousData) => previousData,
  });

  return {planning, isLoading, isError};
};

export const useIsPlanningDocPublished = (planningId: string) => {
  const {planning} = usePlanning(planningId);
  if (!planning) return false;
  return (
    planning.template_type === EditorDocTemplateType.NARRATIVE &&
    planning.status === EditorDocStatus.COMPLETE
  );
};

export const usePlanningDocService = () => {
  const {mutateAsync: updatePlanningDoc} = useMutation({
    mutationFn: ({planningId, update}: {planningId: string; update: EditorDocUpdate}) =>
      getService(EditordocService).updateApiV1EditordocEncryptedIdPut(planningId, update),
    onMutate: async ({planningId, update}) => {
      await queryClient.cancelQueries({queryKey: ['planning', planningId]});

      const previous = queryClient.getQueryData(['planning', planningId]);

      queryClient.setQueryData(['planning', planningId], (old: EditorDoc) => ({
        ...old,
        ...update,
      }));

      return {previous};
    },
  });

  return {updatePlanningDoc};
};

export const useDeletePlanningDoc = (planningId: string) => {
  return useMutation({
    mutationFn: () =>
      getService(EditordocService).deleteApiV1EditordocEncryptedIdDelete(planningId),
    onSuccess: () => {
      queryClient.setQueryData(['planning', 'documents', 'all'], (old: EditorDoc[] | undefined) =>
        old?.filter((doc) => doc.id !== planningId)
      );
      queryClient.invalidateQueries({queryKey: ['planning']});
      queryClient.invalidateQueries({queryKey: ['planning', 'docs']});
    },
  });
};

export const useEditorWorkstreams = (planningId: string | undefined) => {
  const {data: workstreams, isLoading} = useQuery({
    queryKey: ['planning', 'workstreams', planningId],
    queryFn: () =>
      getService(EditordocService).getWorkstreamsApiV1EditordocEncryptedIdWorkstreamsGet(
        planningId!
      ),
    enabled: !!planningId,
  });
  const narrativeWorkstreams =
    workstreams?.filter(
      ({workstream_type}) => workstream_type === EditorWorkstreamType.NARRATIVE
    ) ?? [];
  const contributingWorkstreams =
    workstreams
      ?.filter(
        ({workstream_type}) => workstream_type === EditorWorkstreamType.CONTRIBUTING_NARRATIVE
      )
      .sort(
        (a: EditorWorkstreamAssociation, b: EditorWorkstreamAssociation) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ) ?? [];
  return {narrativeWorkstreams, contributingWorkstreams, isLoading};
};

export const useWorkstreamsUpdate = (planningId: string) => {
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  return useMutation({
    mutationFn: ({
      workstreams,
      action = 'add',
      workstreamType = EditorWorkstreamType.NARRATIVE,
      contributingWorkstreamId = undefined,
    }: {
      workstreams: number[];
      action?: 'add' | 'remove' | 'add_contributing_id' | 'remove_contributing_id';
      workstreamType?: EditorWorkstreamType;
      contributingWorkstreamId?: number;
    }) =>
      getService(EditordocService).workstreamsApiV1EditordocEncryptedIdWorkstreamsPut(
        planningId,
        contributingWorkstreamId,
        workstreamType,
        action,
        workstreams
      ),
    onSuccess: (response) => {
      queryClient.setQueryData(['planning', 'workstreams', planningId], response);
    },
    onError: () => {
      setSnackbarMessage({text: t('error_occurred_please_try_again')});
    },
  });
};

export const useRecentPlanningDocs = () => {
  const [recentPlanningDocs, setRecentPlanningDocs] = useAtom(recentPlanningDocsAtom);

  const addToRecentPlanningDocs = useCallback(
    (id: string) => {
      setRecentPlanningDocs((prev) => {
        // Check if the item already exists and preserve its pin status
        const existingItem = prev.find((recent) => recent.id === id);
        const wasPinned = existingItem?.pinned ?? false;

        // Filter out the item if it already exists
        const recentPlanningDocsCopy = prev.filter((recent) => recent.id !== id);

        // Find the first unpinned item to insert before
        const unpinnedIndex = recentPlanningDocsCopy.findIndex((recent) => !recent.pinned) ?? 0;

        // Insert the new item at the unpinned position, preserving pin status
        recentPlanningDocsCopy.splice(unpinnedIndex, 0, {
          id,
          pinned: wasPinned,
        });

        // If we have more than MAX_RECENT_PLANNING_DOCS items, trim while preserving pinned items
        if (recentPlanningDocsCopy.length >= MAX_RECENT_PLANNING_DOCS) {
          const pinnedItems = recentPlanningDocsCopy.filter((recent) => recent.pinned);
          const unpinnedItems = recentPlanningDocsCopy.filter((recent) => !recent.pinned);
          const maxUnpinnedItems = Math.max(0, MAX_RECENT_PLANNING_DOCS - pinnedItems.length);
          const trimmedUnpinnedItems = unpinnedItems.slice(0, maxUnpinnedItems);
          return [...pinnedItems, ...trimmedUnpinnedItems];
        }

        return recentPlanningDocsCopy;
      });
    },
    [setRecentPlanningDocs]
  );

  const togglePin = useCallback(
    (id: string) => {
      setRecentPlanningDocs((prev) => {
        const existingItem = prev.find((recent) => recent.id === id);

        // If item doesn't exist yet, add it as pinned
        if (!existingItem) {
          return [{id, pinned: true}, ...prev];
        }

        // Otherwise toggle the pin status
        return prev
          .map((recent) => (recent.id === id ? {...recent, pinned: !recent.pinned} : recent))
          .sort((a, b) => {
            if (a.pinned === b.pinned) return 0;
            return a.pinned ? -1 : 1;
          });
      });
    },
    [setRecentPlanningDocs]
  );

  return {
    recentPlanningDocs,
    addToRecentPlanningDocs,
    togglePin,
  };
};

export const useNotifyForMention = () => {
  return useMutation({
    mutationFn: ({
      planningId,
      employeeId,
      mentionNodeId,
      commentPreview,
    }: {
      planningId: string;
      employeeId: string;
      mentionNodeId: string;
      commentPreview?: string;
    }) =>
      getService(EditordocService).notifyForMentionApiV1EditordocEncryptedIdNotifyPost(
        planningId,
        employeeId,
        mentionNodeId,
        commentPreview
      ),
  });
};

export const useStrategyEntries = (planningId: string) => {
  return useQuery({
    queryKey: ['planning', 'strategies', planningId],
    queryFn: () =>
      getService(EditordocService).getStrategyAssociationsApiV1EditordocEncryptedIdStrategiesGet(
        planningId
      ),
    enabled: !!planningId,
  });
};

export const useStrategiesUpdate = () => {
  const createStrategy = useMutation({
    mutationFn: ({
      planningId,
      newStrategy,
    }: {
      planningId: string;
      newStrategy: EditorDocStrategyAssociationCreate;
    }) =>
      getService(EditordocService).createStrategyAssociationApiV1EditordocEncryptedIdStrategiesPost(
        planningId,
        newStrategy
      ),
    onSuccess: (response, {planningId}) => {
      queryClient.setQueryData(
        ['planning', 'strategies', planningId],
        (old: EditorDocStrategyAssociation[] | undefined) => {
          if (!old) return [response];
          return [...old, response];
        }
      );
    },
  });

  const updateStrategy = useMutation({
    mutationFn: ({
      planningId,
      strategyId,
      updatedStrategy,
    }: {
      planningId: string;
      strategyId: number;
      updatedStrategy: EditorDocStrategyAssociationUpdate;
    }) =>
      getService(
        EditordocService
      ).updateStrategyAssociationApiV1EditordocEncryptedIdStrategiesStrategyIdPut(
        strategyId,
        planningId,
        updatedStrategy
      ),
    onSuccess: (response, {planningId}) => {
      queryClient.setQueryData(
        ['planning', 'strategies', planningId],
        (old: EditorDocStrategyAssociation[] | undefined) => {
          if (!old) return [response];
          return old.map((assoc) => (assoc.id === response.id ? response : assoc));
        }
      );
    },
  });

  const deleteStrategy = useMutation({
    mutationFn: ({planningId, strategyId}: {planningId: string; strategyId: number}) =>
      getService(
        EditordocService
      ).deleteStrategyAssociationApiV1EditordocEncryptedIdStrategiesStrategyIdDelete(
        strategyId,
        planningId
      ),
    onSuccess: (response, {planningId}) => {
      queryClient.setQueryData(
        ['planning', 'strategies', planningId],
        (old: EditorDocStrategyAssociation[] | undefined) => {
          if (!old) return [];
          return old.filter((assoc) => assoc.id !== response.id);
        }
      );
    },
  });

  return {createStrategy, updateStrategy, deleteStrategy};
};
